import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { createGradientOrSolid, overrideCssVars } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress } from '@mui/material';
import { useTemplatesStudio } from 'api/useTemplatesStudio';
import useTheme from 'api/useTheme';
import { useUserDetails } from 'api/useUserDetails';
import { DEFAULT_BODY_BLOCKS_ORDER, EEventsType } from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import {
  decodeRichTextContent,
  processRichTextContent
} from 'utils/colorUtils';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import { EToasterStatus, showToast } from 'utils/showToast';
import { z } from 'zod';

import DialogModal from 'components/DialogModal/DialogModal';
import FormHeader from 'components/FormHeader/FormHeader';
import { cn } from 'components/lib/utils';
import { Form } from 'components/ui';

import { TemplateStudioBrandKitContainer } from './BrandKit/Container';
import { BrandKitController } from './Components/BrandKitController';
import { DeviceTypeController } from './Components/DeviceTypeController';
import { FormControllers } from './Components/FormControllers';
import { PersonalizationController } from './Components/PersonalizationController';
import { TitleInput } from './Components/TitleInput';
import { defaultTemplateStudioFormValues } from './defaults';
import { TemplateStudioPreview } from './Preview/Preview';
import { TemplateStudioSidebar } from './Sidebar/Sidebar';
import {
  TEST_PERSONALIZATION_MARGIN_RIGHT_PX,
  TestPersonalizationContainer
} from './TestPersonalization/Container';
import { templateStudioSchema } from './validation-schema';

const HeaderSecondarySection: React.FC = () => {
  return (
    <div className="flex items-center gap-1">
      <PersonalizationController />
      <BrandKitController />
      <FormControllers />
      <DeviceTypeController />
    </div>
  );
};

export const TemplateStudioForm: React.FC<{
  edit?: boolean;
  dup?: boolean;
}> = ({ edit = false, dup = false }) => {
  const navigate = useNavigate();
  const { publisherId, pageId } = useParams();
  const { trackBigQueryEvent } = useBigQueryEventsTracking();
  const { userDetails } = useUserDetails();
  const { getTemplate, updateTemplate, getTemplates, createTemplate } =
    useTemplatesStudio(publisherId, pageId);
  const {
    defaultBodyBlocksOrder,
    setOpenSidebarBlock,
    setScrollToSection,
    isPersonalizationOpen
  } = useTemplateStudioState();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);
  const [
    isSubmittingActiveTemplateModalOpen,
    setIsSubmittingActiveTemplateModalOpen
  ] = useState(false);
  const BUTTON_TEXT = edit ? 'Update' : 'Save';
  const formScrollContainerRef = useRef<HTMLDivElement>(null);

  const initialValues = useMemo(
    () => ({
      ...defaultTemplateStudioFormValues,
      bodyBlocksOrder: defaultBodyBlocksOrder || DEFAULT_BODY_BLOCKS_ORDER
    }),
    [defaultBodyBlocksOrder]
  );

  const form = useForm<z.infer<typeof templateStudioSchema>>({
    resolver: zodResolver(templateStudioSchema),
    mode: 'onChange',
    defaultValues: initialValues
  });

  const {
    formState: { isValid, isDirty, errors },
    watch
  } = form;

  const formValues = watch();

  const initializeForm = useCallback(
    (data: Partial<z.infer<typeof templateStudioSchema>>) => {
      try {
        const formData = {
          ...initialValues,
          ...data,
          richText: data.richText
            ? decodeRichTextContent(data.richText)
            : initialValues.richText
        };

        form.reset(formData);
      } catch (error) {
        console.error('Error initializing form:', error);
      }
    },
    [form, initialValues]
  );

  useEffect(() => {
    if (!edit && !dup) {
      initializeForm(initialValues);
    }
    if (edit && getTemplate.data) {
      initializeForm(getTemplate.data);
    } else if (dup && getTemplate.data) {
      initializeForm({
        ...getTemplate.data,
        templateName: `${getTemplate.data.templateName}_duplicated`
      });
    }

    if ((edit || dup) && getTemplate.isError) {
      showToast({
        message: 'Could not load template',
        status: EToasterStatus.ERROR
      });
      navigate('../');
    }
  }, [
    edit,
    dup,
    getTemplate.data,
    getTemplate.isError,
    getTemplate.error,
    initialValues
  ]);

  useEffect(() => {
    setScrollToSection('header');
    return () => {
      setOpenSidebarBlock(undefined);
      setScrollToSection(undefined);
    };
  }, []);

  const isLoading = useMemo(() => {
    return (
      (edit && getTemplate.isLoading) ||
      createTemplate.isLoading ||
      updateTemplate.isLoading ||
      defaultBodyBlocksOrder === undefined
    );
  }, [
    edit,
    getTemplate.isLoading,
    createTemplate.isLoading,
    updateTemplate.isLoading,
    defaultBodyBlocksOrder
  ]);

  const isUpdateButtonEnabled = useMemo(() => {
    if (isLoading) return false;

    if (dup) return isValid;

    if (edit) {
      return isValid && isDirty;
    }
    return isValid;
  }, [isValid, isDirty, edit, dup, isLoading]);

  const handleLeave = () => {
    setIsUnsavedChangesModalOpen(false);
    navigate('../');
  };

  const handleCloseFormClick = () => {
    if (isDirty) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      handleLeave();
    }
  };

  const onSubmit = async (data: z.infer<typeof templateStudioSchema>) => {
    try {
      const apiData = {
        ...data,
        richText: processRichTextContent(data.richText),
        externalButtons: {
          ...data.externalButtons,
          addToHomeScreenButton: {
            ...data.externalButtons.addToHomeScreenButton,
            iconImage:
              data.externalButtons.addToHomeScreenButton.iconImage ?? '',
            shortcutName:
              data.externalButtons.addToHomeScreenButton.shortcutName ?? ''
          },
          backToGameButton: {
            ...data.externalButtons.backToGameButton,
            buttonImage: data.externalButtons.backToGameButton.buttonImage ?? ''
          }
        }
      };

      if (edit && pageId && !dup) {
        const { templateName } = data;
        await updateTemplate.mutateAsync(
          { form: apiData, templateId: pageId },
          {
            onSuccess: () => {
              showToast({
                message: `${templateName} was updated successfully`,
                status: EToasterStatus.SUCCESS
              });
              getTemplates.refetch();
              handleLeave();
            },
            onError: () => {
              showToast({
                message: `Error updating ${templateName}`,
                status: EToasterStatus.ERROR
              });
            }
          }
        );
      } else {
        await createTemplate.mutateAsync(apiData, {
          onSuccess: () => {
            showToast({
              message: 'Template was created successfully',
              status: EToasterStatus.SUCCESS
            });
            getTemplates.refetch();
            handleLeave();
          },
          onError: (error: any) => {
            showToast({
              message: 'Error creating template',
              description:
                error?.response?.data?.message || 'Unknown error occurred',
              status: EToasterStatus.ERROR
            });
          }
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleFormSubmit = () => {
    if (getTemplate.data?.isActive) {
      setIsSubmittingActiveTemplateModalOpen(true);
    } else {
      form.handleSubmit(onSubmit)();
    }

    trackMixpanelEvent(EEventsType.DASHBOARD_TEMPLATE_ACTION, {
      publisher_id: publisherId,
      user_id: userDetails?.userId,
      company_id: userDetails?.company,
      templateId: getTemplate.data?.templateId,
      templateAction: 'save',
      isActiveTemplate: getTemplate.data?.isActive,
      templateName: getTemplate.data?.templateName
    });

    trackBigQueryEvent({
      eventName: EEventsType.DASHBOARD_TEMPLATE_ACTION,
      publisherId: publisherId,
      userId: userDetails?.userId,
      template_id: getTemplate.data?.templateId,
      action: 'save',
      is_active: getTemplate.data?.isActive,
      template_name: getTemplate.data?.templateName
    });
  };

  const handleConfirmActiveTemplateSubmit = () => {
    setIsSubmittingActiveTemplateModalOpen(false);
    form.handleSubmit(onSubmit)();
  };

  useEffect(() => {
    const cssMappingOverride = {
      '--button-text-color': formValues.brandKit.buttonTextColor,
      '--button-bg-color': createGradientOrSolid(
        formValues.brandKit.buttonColor
      ),
      '--font-family': formValues.brandKit.fontFamily
    };

    overrideCssVars(cssMappingOverride, document);
  }, [
    formValues.brandKit.buttonColor,
    formValues.brandKit.buttonTextColor,
    formValues.brandKit.fontFamily
  ]);

  useEffect(() => {
    return () => {
      const body = document.body;
      if (body.style.pointerEvents === 'none') {
        body.style.pointerEvents = '';
      }
    };
  }, [isUnsavedChangesModalOpen, isSubmittingActiveTemplateModalOpen]);

  return (
    <div className="template-studio-form-container bg-neutral-100 fixed h-[100vh] w-full top-0 left-0 z-[1000] p-1">
      <div
        ref={formScrollContainerRef}
        className="bg-white h-full w-full flex overflow-scroll rounded-xl shadow-lg border border-neutral-200"
      >
        {isLoading && (
          <div className="absolute w-full h-full top-0 left-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
            <CircularProgress />
          </div>
        )}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="template-studio-form flex flex-col w-full mx-auto h-[100vh]"
          >
            <FormHeader
              TitleComponent={TitleInput}
              SecondarySection={HeaderSecondarySection}
              mainButtonText={BUTTON_TEXT}
              onCloseForm={handleCloseFormClick}
              onMainButtonClick={handleFormSubmit}
              closeFormOnSubmit={false}
              closeIcon="fa-arrow-left-to-bracket"
              isFormValid={isUpdateButtonEnabled}
              className="py-3 select-none"
            />
            <div
              className={cn(
                'form-content flex flex-grow gap-4 min-w-0 w-full overflow-x-hidden overflow-y-hidden pt-6 pl-4 transition-[max-width] duration-300 ease-in-out',
                isPersonalizationOpen ? 'pr-0' : 'pr-4'
              )}
              style={
                isPersonalizationOpen
                  ? {
                      maxWidth: `calc(100% - ${TEST_PERSONALIZATION_MARGIN_RIGHT_PX}px)`
                    }
                  : undefined
              }
            >
              <TemplateStudioSidebar />
              <TemplateStudioPreview isLoadingForm={isLoading} />
              <TemplateStudioBrandKitContainer />
              <TestPersonalizationContainer />
            </div>
          </form>
        </Form>
      </div>

      {isSubmittingActiveTemplateModalOpen && (
        <DialogModal
          isOpen={isSubmittingActiveTemplateModalOpen}
          title="Are you sure?"
          description="When you change an active template, the store will update to reflect its new values."
          modalActions={{
            onConfirm: {
              label: 'Save',
              action: handleConfirmActiveTemplateSubmit
            },
            onClose: {
              label: 'Cancel',
              action: () => setIsSubmittingActiveTemplateModalOpen(false),
              variant: 'outline'
            }
          }}
        />
      )}

      {isUnsavedChangesModalOpen && (
        <DialogModal
          isOpen={isUnsavedChangesModalOpen}
          title={'Unsaved Changes'}
          description={
            'You’ve made changes to the template that haven’t been saved. Leaving now will discard your changes.'
          }
          modalActions={{
            onConfirm: {
              label: 'Continue',
              action: () => {
                setIsUnsavedChangesModalOpen(false);
              }
            },
            onClose: {
              label: 'Leave',
              action: handleLeave,
              variant: 'outline'
            }
          }}
        />
      )}
    </div>
  );
};
